"use client";
import "maplibre-gl/dist/maplibre-gl.css";
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import maplibregl, { Map as MlMap } from "maplibre-gl";
import { Icons } from "@/components/Icons";
import { Badge, Button } from "@/components/ui";
import { locationPinApi, type LocationPinCoverage } from "@/api/locationPin";
import { ensureStorefrontApiKey } from "@/api/storefrontKey";
import {
  DEFAULT_CENTER,
  DEFAULT_MAP_STYLE,
  DEFAULT_ZOOM,
  autocompleteAddress,
  resolvePlaceId,
  reverseGeocode,
  type PlaceSuggestion,
  type ResolvedAddress,
} from "@/lib/location";

/**
 * PinLocationScreen (SMS location-pin feature) — the public,
 * unauthenticated page a call-centre agent's link opens.
 *
 * NO customer data appears anywhere on this page, and none is fetched. The
 * GET response carries only {valid, expiresAt}; coverage carries only the
 * restaurant's own delivery offer for a coordinate the customer chose.
 * That emptiness is what makes a leaked or guessed link harmless — it is a
 * write-only capability, never a read one. Do not add a name, a phone, an
 * order or a branch list here to make the page friendlier.
 *
 * R6 — the page used to be GPS-or-nothing: a single `getCurrentPosition`
 * call, a non-draggable marker, and a dead end if permission was denied.
 * Pilot feedback was that this is unusable in practice. Customers deny the
 * prompt by reflex, GPS lands 200m off indoors, and in much of the region
 * the delivery-critical part of an address (which gate, which tower, the
 * unnamed side street) is something a person points at on a map — it is
 * not derivable from a fix. So the pin now moves: search, drag, or GPS.
 *
 * The original concern behind "GPS or nothing" was real and is preserved
 * rather than discarded: an approximate pin that LOOKS authoritative is
 * worse for a driver than an obviously rough one. That is what `pinSource`
 * is for. A pin the customer moved is submitted as `manual` and carries no
 * accuracy radius; only an untouched device fix is submitted as `gps` with
 * its measured accuracy. The agent and the driver always see which they
 * got. The screen never fabricates the distinction — any map movement
 * after a fix downgrades it.
 *
 * Coverage is advisory, never a gate: see `handleConfirm`.
 */

type TokenState = "checking" | "invalid" | "valid";
/** Why the map is where it is — drives what we submit, not just the copy. */
type PinSource = "gps" | "manual";
type GeoState = "idle" | "requesting" | "denied" | "unavailable" | "unsupported" | "granted";
type CoverageState = "idle" | "checking" | "done";

interface Coords {
  lat: number;
  lng: number;
}

const GEOCODE_DEBOUNCE_MS = 500;
const SEARCH_DEBOUNCE_MS = 350;
const MIN_SEARCH_LENGTH = 3;

export function PinLocationScreen() {
  const { token = "" } = useParams<{ token: string }>();
  const { t } = useTranslation();

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  // Set before any flyTo WE initiate (GPS or a search result) so the
  // resulting `moveend` isn't mistaken for the customer dragging the map —
  // which would immediately downgrade a fresh GPS fix to `manual`.
  const ignoreNextMove = useRef(false);
  const geocodeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic counters: a slow reverse-geocode or coverage response from a
  // superseded position must never overwrite a newer one.
  const geocodeSeq = useRef(0);
  const coverageSeq = useRef(0);

  const [tokenState, setTokenState] = useState<TokenState>("checking");
  const [geoState, setGeoState] = useState<GeoState>("idle");
  const [pinSource, setPinSource] = useState<PinSource>("manual");
  const [gpsAccuracy, setGpsAccuracy] = useState<number | null>(null);
  const [picked, setPicked] = useState<ResolvedAddress | null>(null);
  const [resolving, setResolving] = useState(false);
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [coverage, setCoverage] = useState<LocationPinCoverage | null>(null);
  const [coverageState, setCoverageState] = useState<CoverageState>("idle");
  const [submitState, setSubmitState] = useState<"idle" | "submitting" | "sent" | "error">(
    "idle",
  );

  // Step 1: resolve the token. Never logs the token or any response field —
  // an invalid, expired, and already-used token are indistinguishable by
  // design, so all three collapse to the same "invalid" UI state.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setTokenState("invalid");
      return;
    }
    // The pin API goes through the online-ordering BFF, which needs the
    // tenant X-API-KEY. This page sits outside BranchProvider, so nothing
    // else fetches the tenant config — but the slug is in our own URL.
    // `ensureStorefrontApiKey` always settles (opening the gate with a null
    // key on failure), so this can never leave the page waiting forever.
    void ensureStorefrontApiKey()
      .then(() => locationPinApi.check(token))
      .then((res) => {
        if (cancelled) return;
        setTokenState(res.valid ? "valid" : "invalid");
      })
      .catch(() => {
        if (cancelled) return;
        setTokenState("invalid");
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const runCoverage = useCallback(
    async (c: Coords) => {
      if (!token) return;
      const seq = ++coverageSeq.current;
      setCoverageState("checking");
      try {
        const res = await locationPinApi.coverage(token, c.lat, c.lng);
        if (seq !== coverageSeq.current) return;
        setCoverage(res);
      } catch {
        // Fail soft and silent: an unreachable coverage endpoint must not
        // put an error in front of a customer whose only job is to drop a
        // pin. `served: false` with nothing else reads as "we can't
        // confirm", which is exactly the truth here.
        if (seq !== coverageSeq.current) return;
        setCoverage({ served: false });
      } finally {
        if (seq === coverageSeq.current) setCoverageState("done");
      }
    },
    [token],
  );

  /** Resolve whatever is under the pin: an address line to show, and
   *  whether anyone delivers there. Both are display-only — the coordinate
   *  is always the thing actually submitted. */
  const resolveCenter = useCallback(async () => {
    const map = mapRef.current;
    if (!map) return;
    const c = map.getCenter();
    const here: Coords = { lat: c.lat, lng: c.lng };
    void runCoverage(here);

    const seq = ++geocodeSeq.current;
    setResolving(true);
    try {
      const result = await reverseGeocode(here.lat, here.lng);
      if (seq !== geocodeSeq.current) return;
      setPicked(
        result ?? {
          street: "",
          city: "",
          state: "",
          postalCode: "",
          country: "",
          // No address is a perfectly normal answer for the unnamed spot
          // this page exists to capture — show the coordinate rather than
          // an empty card, and never block on it.
          label: `${here.lat.toFixed(5)}, ${here.lng.toFixed(5)}`,
          lat: here.lat,
          lng: here.lng,
        },
      );
    } finally {
      if (seq === geocodeSeq.current) setResolving(false);
    }
  }, [runCoverage]);

  /** Ask the device where it is and fly there. Distinguishes a genuine
   *  permission denial (code 1) — the only case where "change your browser
   *  settings" is correct advice — from a transient failure to get a fix
   *  (code 2/3). Unlike R5 this is no longer a dead end either way: the
   *  customer can still search or drag. */
  const locateMe = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoState("unsupported");
      return;
    }
    setGeoState("requesting");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoState("granted");
        setPinSource("gps");
        setGpsAccuracy(Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null);
        ignoreNextMove.current = true;
        mapRef.current?.flyTo({
          center: [pos.coords.longitude, pos.coords.latitude],
          zoom: 17,
        });
        void resolveCenter();
      },
      (err) => {
        setGeoState(err?.code === 1 ? "denied" : "unavailable");
      },
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  }, [resolveCenter]);

  // Step 2: mount the map once the token is confirmed valid, and try for a
  // device fix straight away — GPS remains the best answer when it works,
  // it is just no longer the only one.
  useEffect(() => {
    if (tokenState !== "valid" || !containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: DEFAULT_MAP_STYLE,
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    mapRef.current = map;

    map.on("moveend", () => {
      if (ignoreNextMove.current) {
        ignoreNextMove.current = false;
        return;
      }
      // The customer moved the map themselves. Any GPS fix we had is now
      // stale, and its accuracy figure would be a lie about a point the
      // device never measured — drop both.
      setPinSource("manual");
      setGpsAccuracy(null);
      if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
      geocodeTimer.current = setTimeout(() => {
        geocodeTimer.current = null;
        void resolveCenter();
      }, GEOCODE_DEBOUNCE_MS);
    });

    map.on("load", () => {
      locateMe();
    });

    return () => {
      map.remove();
      mapRef.current = null;
      if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenState]);

  // Debounced forward search, biased to wherever the map currently is.
  useEffect(() => {
    if (!search || search.trim().length < MIN_SEARCH_LENGTH) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      const center = mapRef.current?.getCenter();
      const results = await autocompleteAddress(search, {
        biasPosition: center ? [center.lng, center.lat] : undefined,
        maxResults: 6,
      });
      if (!cancelled) setSuggestions(results);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search]);

  const pickSuggestion = async (s: PlaceSuggestion) => {
    setSearch("");
    setSuggestions([]);
    setResolving(true);
    const resolved = await resolvePlaceId(s.placeId);
    setResolving(false);
    if (!resolved || !mapRef.current) return;
    // A searched address is a hand-picked point, not a measurement.
    setPinSource("manual");
    setGpsAccuracy(null);
    setPicked(resolved);
    ignoreNextMove.current = true;
    mapRef.current.flyTo({ center: [resolved.lng, resolved.lat], zoom: 17 });
    void runCoverage({ lat: resolved.lat, lng: resolved.lng });
  };

  const handleConfirm = async () => {
    const map = mapRef.current;
    if (!map || submitState === "submitting" || submitState === "sent") return;
    // Read the coordinate off the map at submit time rather than trusting
    // `picked`, which trails it by a debounce — the pin the customer is
    // looking at is the pin they mean.
    const c = map.getCenter();
    setSubmitState("submitting");
    try {
      await locationPinApi.submit(token, {
        lat: c.lat,
        lng: c.lng,
        // Only an untouched device fix carries an accuracy radius.
        accuracyMeters: pinSource === "gps" ? gpsAccuracy : null,
        pinSource,
        address: picked?.label ?? null,
        placeName: picked?.street || null,
      });
      setSubmitState("sent");
    } catch {
      setSubmitState("error");
    }
  };

  if (tokenState === "checking") {
    return (
      <div style={outerStyle}>
        <div style={cardStyle}>
          <p style={bodyText}>{t("v2.pin_location.checking", "Loading…")}</p>
        </div>
      </div>
    );
  }

  if (tokenState === "invalid") {
    return (
      <div style={outerStyle}>
        <div style={cardStyle}>
          <Icons.pin size={28} style={{ color: "var(--ink-3)" }} />
          <p style={titleText}>
            {t("v2.pin_location.invalid_title", "This link is no longer valid")}
          </p>
          <p style={bodyText}>
            {t(
              "v2.pin_location.invalid_body",
              "It may have expired or already been used. Ask the restaurant to send a new one.",
            )}
          </p>
        </div>
      </div>
    );
  }

  if (submitState === "sent") {
    return (
      <div style={outerStyle}>
        <div style={cardStyle}>
          <Icons.pin size={28} style={{ color: "var(--primary-600)" }} />
          <p style={titleText}>
            {t("v2.pin_location.sent", "Location shared. You can close this page.")}
          </p>
          {picked?.label && <p style={bodyText}>{picked.label}</p>}
        </div>
      </div>
    );
  }

  const addressLine =
    picked?.street || picked?.label || t("v2.pin_location.drag_hint", "Move the map to your exact spot");
  const subLine = picked
    ? [picked.neighborhood, picked.city, picked.state].filter(Boolean).join(", ")
    : "";

  // Coverage copy. `served: false` is deliberately soft ("we can't confirm
  // delivery here"), because it also covers a failed lookup — see
  // LocationPinCoverage. It never disables the confirm button.
  const coverageLabel =
    coverageState === "checking"
      ? t("v2.pin_location.coverage_checking", "Checking delivery area…")
      : coverage?.served
        ? coverage.branchName
          ? t("v2.pin_location.coverage_served_branch", {
              branch: coverage.branchName,
              defaultValue: "Delivered from {{branch}}",
            })
          : t("v2.pin_location.coverage_served", "Inside the delivery area")
        : coverageState === "done"
          ? t("v2.pin_location.coverage_unserved", "We can't confirm delivery to this spot")
          : null;
  const coverageTone =
    coverageState === "checking" ? "warn" : coverage?.served ? "success" : "danger";

  const feeLine =
    coverage?.served && coverage.feeAmount != null
      ? `${coverage.feeAmount} ${coverage.feeCurrency ?? ""}`.trim()
      : null;
  const etaLine =
    coverage?.served && coverage.minDeliveryDurationInMinutes != null
      ? t("v2.pin_location.eta", {
          min: coverage.minDeliveryDurationInMinutes,
          max: coverage.maxDeliveryDurationInMinutes ?? coverage.minDeliveryDurationInMinutes,
          defaultValue: "{{min}}–{{max}} min",
        })
      : null;

  return (
    <div style={pinShellStyle}>
      <div style={{ flex: 1, position: "relative", overflow: "hidden", background: "var(--surface-2)" }}>
        <div data-testid="pin-map" ref={containerRef} style={{ position: "absolute", inset: 0 }} />
        {/* Fixed-centre pin: the map moves underneath it. Same idiom as the
            storefront's own address picker, so a customer who has ordered
            before already knows how to use this. */}
        <div
          aria-hidden
          data-testid="pin-crosshair"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
            zIndex: 5,
            color: "var(--primary-600)",
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.25))",
          }}
        >
          <Icons.pin size={36} />
        </div>

        <button
          type="button"
          onClick={locateMe}
          aria-label={t("v2.map_picker.use_my_location", "Use my location")}
          style={locateButtonStyle}
        >
          <Icons.pin size={18} />
        </button>
      </div>

      <div style={drawerStyle}>
        {/* Search — the part that makes this usable when GPS is refused or
            simply wrong, and the only way to reach an address the customer
            is not standing at. */}
        <div style={{ position: "relative" }}>
          <div style={searchBoxStyle}>
            <Icons.search size={18} style={{ color: "var(--ink-3)" }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("v2.map_picker.search_placeholder", "Search for an area or street")}
              aria-label={t("v2.map_picker.search_placeholder", "Search for an area or street")}
              style={searchInputStyle}
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setSuggestions([]);
                }}
                aria-label={t("v2.common.close", "Clear")}
                style={iconButtonStyle}
              >
                <Icons.close size={16} />
              </button>
            )}
          </div>
          {suggestions.length > 0 && (
            <div style={suggestionsStyle}>
              {suggestions.map((s) => (
                <button
                  key={s.placeId}
                  type="button"
                  onClick={() => void pickSuggestion(s)}
                  style={suggestionRowStyle}
                >
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{s.label}</div>
                  {s.secondaryText && (
                    <div style={{ fontSize: 12, color: "var(--ink-2)" }}>{s.secondaryText}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div style={pinBadgeStyle}>
            <Icons.pin size={16} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={eyebrowText}>
              {resolving
                ? t("v2.map_picker.resolving", "Resolving…")
                : t("v2.map_picker.pinned_location", "Pinned location")}
            </div>
            <div style={addressText} dir="auto">
              {addressLine}
            </div>
            {subLine && (
              <div style={{ ...bodyText, marginTop: 2 }} dir="auto">
                {subLine}
              </div>
            )}
            {coverageLabel && (
              <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
                <Badge tone={coverageTone}>{coverageLabel}</Badge>
                {feeLine && <Badge tone="neutral">{feeLine}</Badge>}
                {etaLine && <Badge tone="neutral">{etaLine}</Badge>}
              </div>
            )}
          </div>
        </div>

        {/* Permission state is a hint now, not a wall — the pin still works
            without it, so this never replaces the map or the confirm CTA. */}
        {(geoState === "denied" || geoState === "unavailable" || geoState === "unsupported") && (
          <p style={{ ...bodyText, color: "var(--ink-3)" }}>
            {geoState === "denied"
              ? t(
                  "v2.pin_location.denied_hint",
                  "Location is off, so we can't place the pin for you. Search or drag the map to your spot instead.",
                )
              : geoState === "unsupported"
                ? t(
                    "v2.pin_location.unsupported_hint",
                    "This browser can't share your location. Search or drag the map to your spot instead.",
                  )
                : t(
                    "v2.pin_location.unavailable_hint",
                    "We couldn't get a fix. Search or drag the map to your spot instead.",
                  )}
          </p>
        )}

        {submitState === "error" && (
          <p style={{ ...bodyText, color: "var(--danger-600, #c0392b)" }}>
            {t("v2.pin_location.error", "Couldn't send your location. Try again.")}
          </p>
        )}

        <Button full size="lg" onClick={handleConfirm} disabled={submitState === "submitting"}>
          {t("v2.pin_location.confirm_cta", "Share my location")}
        </Button>
      </div>
    </div>
  );
}

// The pin page sits OUTSIDE BranchLayout/PhoneShell, so nothing above it
// establishes a height: `#root` has no height rule and html/body's `height:
// 100%` does not reach through it. A `height: 100%` here therefore resolved
// to 0, collapsing the map and pushing the absolutely-positioned drawer
// above the viewport (bottom of a zero-height box) — the page looked blank.
// Size against the viewport instead; `min-height: 100vh` is the fallback
// where `dvh` is unsupported.
const pinShellStyle: CSSProperties = {
  position: "relative",
  height: "100dvh",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
};

const outerStyle: CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  background: "var(--surface-2)",
};

const cardStyle: CSSProperties = {
  maxWidth: 360,
  width: "100%",
  textAlign: "center",
  background: "var(--surface-1)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-card)",
  padding: 24,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
};

const drawerStyle: CSSProperties = {
  position: "absolute",
  left: 12,
  right: 12,
  bottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
  zIndex: 20,
  borderRadius: 18,
  background: "var(--surface-1)",
  boxShadow: "0 -10px 28px -12px rgba(0,0,0,0.2)",
  padding: 14,
  maxHeight: "70%",
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const searchBoxStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  height: 42,
  padding: "0 12px",
  background: "var(--surface-2)",
  borderRadius: "var(--radius-card)",
  border: "1px solid var(--border)",
};

const searchInputStyle: CSSProperties = {
  flex: 1,
  border: 0,
  background: "transparent",
  outline: "none",
  fontSize: 14,
  fontFamily: "inherit",
  color: "var(--ink-1)",
  minWidth: 0,
};

const iconButtonStyle: CSSProperties = {
  background: "transparent",
  border: 0,
  color: "var(--ink-3)",
  cursor: "pointer",
  padding: 4,
  display: "grid",
  placeItems: "center",
};

const suggestionsStyle: CSSProperties = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: "calc(100% + 8px)",
  zIndex: 10,
  background: "var(--surface-1)",
  borderRadius: 12,
  border: "1px solid var(--border)",
  boxShadow: "0 8px 24px -12px rgba(0,0,0,0.3)",
  overflow: "hidden",
  maxHeight: 220,
  overflowY: "auto",
};

const suggestionRowStyle: CSSProperties = {
  display: "block",
  width: "100%",
  textAlign: "start",
  background: "transparent",
  border: 0,
  borderBottom: "1px solid var(--border)",
  padding: "10px 12px",
  cursor: "pointer",
  fontFamily: "inherit",
  color: "var(--ink-1)",
};

const locateButtonStyle: CSSProperties = {
  position: "absolute",
  insetInlineEnd: 12,
  bottom: 12,
  zIndex: 6,
  width: 40,
  height: 40,
  borderRadius: 20,
  border: "1px solid var(--border)",
  background: "var(--surface-1)",
  color: "var(--primary-600)",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  boxShadow: "0 4px 12px -6px rgba(0,0,0,0.35)",
};

const pinBadgeStyle: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 10,
  background: "var(--primary-50)",
  display: "grid",
  placeItems: "center",
  color: "var(--primary-700)",
  flexShrink: 0,
};

const eyebrowText: CSSProperties = {
  fontSize: 11,
  color: "var(--ink-3)",
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: "uppercase",
};

const addressText: CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: "var(--ink-1)",
  marginTop: 2,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const titleText: CSSProperties = {
  fontSize: 15,
  fontWeight: 700,
  color: "var(--ink-1)",
  margin: 0,
};

const bodyText: CSSProperties = {
  fontSize: 13,
  color: "var(--ink-2)",
  margin: 0,
};
